import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import { API_ERROR_HANDLED } from './api-error.interceptor';
import {
  AddRpmItemNoteDto,
  CaptureGrassItemDto,
  GrassCatcherDto,
  GrassCatcherItemDto,
  ReorderRpmItemsDto,
  RpmCategoryDto,
  RpmItemDto,
  RpmItemNoteDto,
  RpmPlanDto,
  RpmSummaryDto,
  RpmWeekDto,
  RpmWeekReviewDto,
  SaveRpmCategoryDto,
  SaveRpmItemDto,
  SaveRpmWeekReviewDto,
  SetRpmItemActionCompleteDto,
  SetRpmItemStatusDto,
} from './rpm.dto';

/**
 * The RPM Plan API — `com.diamond_ridge.os.rpm.RpmController`, built to
 * `docs/backend-spec-rpm-plan.md` §4 and answered in
 * `Source Code/os/docs/backend-response-october-23-rpm-plan.md`.
 *
 * ONE USER ACTION IS ONE CALL. There is no whole-plan write: ticking an action,
 * reordering, adding a note are each their own request, and each write answers
 * what it changed so the client never re-reads to find out.
 *
 * ADDRESSED BY PERSON, `/api/v1/rpm/{personaCode}/…`, not the `/me/rpm` twin.
 * The server serves both from one code path (`MyRpmController` resolves the
 * caller and hands straight to `RpmController`), so for the signed-in person
 * they are the same call. The addressed form is used because the screen shows
 * the CURRENT persona — the shell's "View As" can make that somebody other than
 * the caller — and `/me` would then answer the caller's plan under the other
 * person's name. Addressed, a manager sees their report's plan, the CEO's note
 * on it is the very call the spec asks for (D8, §4.3), and anything else the
 * server refuses with its own reason (`RpmAccess`).
 *
 * THE BLOCKS ARE AT `/items`, not the spec's `/rpm-items`: the backend's route
 * rule refuses a segment repeating `rpm` two segments up.
 *
 * WHO REPORTS A FAILURE. The writes behind a modal — add / edit a block, promote,
 * the weekly review, add / edit a category — carry `API_ERROR_HANDLED`: the modal
 * stays open and shows the server's reason at its top (standards §8, "an add/edit
 * modal waits for the server"), so a dialog over it would cover the form the
 * person came back to fix. Every other call — the reads and the one-click writes
 * (tick, status, reorder, delete, note, capture) — is left to the shared error
 * dialog, which is how the rest of the app reports a refused click.
 */
@Injectable({ providedIn: 'root' })
export class RpmApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = no API, and RPM has no other source. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  /** `apiUrl` already carries `/api/v1`; appending it again 404s every call. */
  private url(personaCode: string, path: string): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}/rpm/${personaCode}${path}`;
  }

  // ── The plan ───────────────────────────────────────────────────────────────

  /** `GET /plans` — open blocks plus those finished this week, in order, with the categories. */
  readPlan(personaCode: string): Observable<RpmPlanDto> {
    return this.http.get<RpmPlanDto>(this.url(personaCode, '/plans'));
  }

  /** `POST /items` — placed last, status TODO. Behind the Add Block modal. */
  addItem(personaCode: string, body: SaveRpmItemDto): Observable<RpmItemDto> {
    return this.http.post<RpmItemDto>(this.url(personaCode, '/items'), body, formHandled());
  }

  /** `PUT /items/{guid}` — the whole editable block; actions reconciled by guid. Behind Edit Block. */
  saveItem(personaCode: string, rpmItemGuid: string, body: SaveRpmItemDto): Observable<RpmItemDto> {
    return this.http.put<RpmItemDto>(this.url(personaCode, `/items/${rpmItemGuid}`), body, formHandled());
  }

  /** `DELETE /items/{guid}` — soft; the block stays in the history of the weeks it was on. 204. */
  deleteItem(personaCode: string, rpmItemGuid: string): Observable<void> {
    return this.http.delete<void>(this.url(personaCode, `/items/${rpmItemGuid}`));
  }

  /** `PUT /items/{guid}/status` — sets or clears `completedTs`. */
  setStatus(personaCode: string, rpmItemGuid: string, body: SetRpmItemStatusDto): Observable<RpmItemDto> {
    return this.http.put<RpmItemDto>(this.url(personaCode, `/items/${rpmItemGuid}/status`), body);
  }

  /** `PUT /items/{guid}/actions/{actionGuid}` — answers the whole block with its re-derived status. */
  setActionComplete(
    personaCode: string,
    rpmItemGuid: string,
    rpmItemActionGuid: string,
    body: SetRpmItemActionCompleteDto,
  ): Observable<RpmItemDto> {
    return this.http.put<RpmItemDto>(this.url(personaCode, `/items/${rpmItemGuid}/actions/${rpmItemActionGuid}`), body);
  }

  /** `PUT /plans/order` — the new order; answers the plan. */
  reorder(personaCode: string, body: ReorderRpmItemsDto): Observable<RpmPlanDto> {
    return this.http.put<RpmPlanDto>(this.url(personaCode, '/plans/order'), body);
  }

  /** `POST /items/{guid}/notes` — the server stamps the author (the caller) and the time. */
  addNote(personaCode: string, rpmItemGuid: string, body: AddRpmItemNoteDto): Observable<RpmItemNoteDto> {
    return this.http.post<RpmItemNoteDto>(this.url(personaCode, `/items/${rpmItemGuid}/notes`), body);
  }

  // ── The week history, the review, the year ─────────────────────────────────

  /** `GET /weeks/{sunday}` — one week as it stood, with each block's outcome and the counts. */
  readWeek(personaCode: string, weekStartDt: string): Observable<RpmWeekDto> {
    return this.http.get<RpmWeekDto>(this.url(personaCode, `/weeks/${weekStartDt}`));
  }

  /**
   * `PUT /weeks/{sunday}/review` — create or replace. Both answers empty deletes
   * it and the server answers an empty body, which arrives here as null.
   */
  saveReview(personaCode: string, weekStartDt: string, body: SaveRpmWeekReviewDto): Observable<RpmWeekReviewDto | null> {
    return this.http.put<RpmWeekReviewDto | null>(this.url(personaCode, `/weeks/${weekStartDt}/review`), body, formHandled());
  }

  /** `GET /summary?year=` — the year-to-date panel's counts. */
  readSummary(personaCode: string, year: number): Observable<RpmSummaryDto> {
    return this.http.get<RpmSummaryDto>(this.url(personaCode, '/summary'), { params: { year } });
  }

  // ── Categories ─────────────────────────────────────────────────────────────

  /** `POST /categories` — a duplicate name (ignoring case) is a 409. Behind Add Category. */
  addCategory(personaCode: string, body: SaveRpmCategoryDto): Observable<RpmCategoryDto> {
    return this.http.post<RpmCategoryDto>(this.url(personaCode, '/categories'), body, formHandled());
  }

  /** `PUT /categories/{guid}` — rename and recolour, one call. Behind Edit Category. */
  saveCategory(personaCode: string, categoryGuid: string, body: SaveRpmCategoryDto): Observable<RpmCategoryDto> {
    return this.http.put<RpmCategoryDto>(this.url(personaCode, `/categories/${categoryGuid}`), body, formHandled());
  }

  // ── Grass Catcher ──────────────────────────────────────────────────────────

  /** `GET /grass-catcher` — the captured items (and the same category list). */
  readGrassCatcher(personaCode: string): Observable<GrassCatcherDto> {
    return this.http.get<GrassCatcherDto>(this.url(personaCode, '/grass-catcher'));
  }

  /** `POST /grass-catcher-items` — the server stamps `capturedTs`. */
  captureGrassItem(personaCode: string, body: CaptureGrassItemDto): Observable<GrassCatcherItemDto> {
    return this.http.post<GrassCatcherItemDto>(this.url(personaCode, '/grass-catcher-items'), body);
  }

  /** `DELETE /grass-catcher-items/{guid}` — 204. */
  discardGrassItem(personaCode: string, itemGuid: string): Observable<void> {
    return this.http.delete<void>(this.url(personaCode, `/grass-catcher-items/${itemGuid}`));
  }

  /**
   * `POST /grass-catcher-items/{guid}/promotion` — in one transaction: a block
   * titled with the item's text, in its category, placed last, a note saying it
   * was promoted, and the item gone. Behind the Promote modal.
   */
  /**
   * Promote a caught item with the WHOLE block — the same body as adding one
   * (`AddRpmItem`), so the person fills in the full Add Block form and Save is
   * one call: the block is made, the note records the item's wording, and the
   * item leaves the Grass Catcher, in one transaction.
   */
  promoteGrassItem(personaCode: string, itemGuid: string, body: SaveRpmItemDto): Observable<RpmItemDto> {
    return this.http.post<RpmItemDto>(this.url(personaCode, `/grass-catcher-items/${itemGuid}/promotion`), body, formHandled());
  }
}

/** Options for a write whose modal shows the refusal itself (see the class comment). */
function formHandled(): { context: HttpContext } {
  return { context: new HttpContext().set(API_ERROR_HANDLED, true) };
}
